import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, roleHome } from "./lib/auth";
import LoginPage from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import AppShell from "./components/AppShell";
import AdminDashboard from "./pages/admin/Dashboard";
import ClientsPage from "./pages/admin/Clients";
import ClientDetail from "./pages/admin/ClientDetail";
import AdminPortfolios from "./pages/admin/Portfolios";
import AdminStaff from "./pages/admin/Staff";
import RAPanel from "./pages/ra/RAPanel";
import StrategyDetail from "./pages/ra/StrategyDetail";
import CustomerPortfolio from "./pages/customer/CustomerPortfolio";
import EngineHistory from "./pages/admin/EngineHistory";
import SendAlert from "./pages/admin/SendAlert";
import NotificationHistory from "./pages/admin/NotificationHistory";
import AdminIndices from "./pages/admin/Indices";
import LoginHistory from "./pages/admin/LoginHistory";
import "@/App.css";

function ProtectedRoute({ allowed, children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-zinc-500">
        Loading...
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  }
  if (allowed && !allowed.includes(user.role)) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return <AppShell>{children}</AppShell>;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-zinc-500">
        Loading...
      </div>
    );
  }
  return <Navigate to={user ? roleHome(user.role) : "/login"} replace />;
}

function AppRouter() {
  // Detect URL fragment session_id during render to avoid race conditions
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<RootRedirect />} />

      {/* Admin */}
      <Route
        path="/admin/dashboard"
        element={<ProtectedRoute allowed={["admin"]}><AdminDashboard /></ProtectedRoute>}
      />
      <Route
        path="/admin/clients"
        element={<ProtectedRoute allowed={["admin"]}><ClientsPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/clients/:id"
        element={<ProtectedRoute allowed={["admin"]}><ClientDetail /></ProtectedRoute>}
      />
      <Route
        path="/admin/portfolios"
        element={<ProtectedRoute allowed={["admin"]}><AdminPortfolios /></ProtectedRoute>}
      />
      <Route
        path="/admin/staff"
        element={<ProtectedRoute allowed={["admin"]}><AdminStaff /></ProtectedRoute>}
      />
      <Route
        path="/admin/recommendations"
        element={<ProtectedRoute allowed={["admin"]}><RAPanel /></ProtectedRoute>}
      />

      {/* CRM (reuses admin pages) */}
      <Route
        path="/crm/clients"
        element={<ProtectedRoute allowed={["crm"]}><ClientsPage /></ProtectedRoute>}
      />
      <Route
        path="/crm/clients/:id"
        element={<ProtectedRoute allowed={["crm"]}><ClientDetail /></ProtectedRoute>}
      />

      {/* RA */}
      <Route
        path="/ra/strategies"
        element={<ProtectedRoute allowed={["ra", "admin"]}><RAPanel tab="strategies" /></ProtectedRoute>}
      />
      <Route
        path="/ra/strategies/:id"
        element={<ProtectedRoute allowed={["ra", "admin"]}><StrategyDetail /></ProtectedRoute>}
      />
      <Route
        path="/ra/recommendations"
        element={<ProtectedRoute allowed={["ra", "admin"]}><RAPanel tab="recommendations" /></ProtectedRoute>}
      />
      <Route
        path="/ra/bhavcopy"
        element={<ProtectedRoute allowed={["ra", "admin"]}><RAPanel tab="bhavcopy" /></ProtectedRoute>}
      />
      <Route
        path="/ra/engine"
        element={<ProtectedRoute allowed={["ra", "admin"]}><RAPanel tab="engine" /></ProtectedRoute>}
      />
      <Route
        path="/ra/backtest"
        element={<ProtectedRoute allowed={["ra", "admin"]}><RAPanel tab="backtest" /></ProtectedRoute>}
      />
      <Route
        path="/admin/engine-history"
        element={<ProtectedRoute allowed={["admin", "ra"]}><EngineHistory /></ProtectedRoute>}
      />
      <Route
        path="/admin/send-alert"
        element={<ProtectedRoute allowed={["admin", "ra"]}><SendAlert /></ProtectedRoute>}
      />
      <Route
        path="/admin/notifications"
        element={<ProtectedRoute allowed={["admin", "ra"]}><NotificationHistory /></ProtectedRoute>}
      />
      <Route
        path="/admin/indices"
        element={<ProtectedRoute allowed={["admin"]}><AdminIndices /></ProtectedRoute>}
      />
      <Route
        path="/admin/login-history"
        element={<ProtectedRoute allowed={["admin"]}><LoginHistory /></ProtectedRoute>}
      />

      {/* Customer */}
      <Route
        path="/portfolio"
        element={<ProtectedRoute allowed={["customer"]}><CustomerPortfolio /></ProtectedRoute>}
      />
      <Route
        path="/portfolio/:pid"
        element={<ProtectedRoute allowed={["customer"]}><CustomerPortfolio /></ProtectedRoute>}
      />
      <Route
        path="/portfolio/holdings"
        element={<ProtectedRoute allowed={["customer"]}><CustomerPortfolio /></ProtectedRoute>}
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
